import React, { useEffect, useState } from "react";
import { api } from "@/skate/lib/api";
import { useAuth } from "@/skate/context/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import RankBadge from "@/skate/components/RankBadge";
import SkaterAvatar from "@/skate/components/SkaterAvatar";
import { tierForElo, progressToNext, nextTier, FORMAT_LABEL } from "@/skate/lib/rank";
import { Button } from "@/components/ui/button";
import { Bell, Swords, Trophy, Flame, ChevronRight, MapPin, LogOut } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value, accent = false }) => (
  <div className="border border-zinc-800 p-4">
    <div className="font-mono text-[10px] tracking-widest text-zinc-500">{label}</div>
    <div className={`font-display text-3xl mt-1 ${accent ? "text-[#FACC15]" : "text-white"}`}>{value}</div>
  </div>
);

export const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [recent, setRecent] = useState([]);
  const [pending, setPending] = useState([]);

  const load = async () => {
    try {
      const [ov, ma, pe] = await Promise.all([
        api.get("/stats/overview"),
        api.get("/matches?limit=5"),
        api.get("/matches/pending"),
      ]);
      setOverview(ov.data);
      setRecent(ma.data);
      setPending(pe.data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => { load(); }, []);

  const handleConfirm = async (matchId, action) => {
    try {
      await api.patch(`/matches/${matchId}`, { action });
      toast.success(action === "CONFIRM" ? "Match confirmed. ELO updated." : "Match disputed. Awaiting review.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const tier = tierForElo(user?.elo);
  const next = nextTier(user?.elo);
  const pct = progressToNext(user?.elo);

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-zinc-500">WELCOME BACK</div>
          <div className="font-display text-2xl">@{user?.username}</div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/skate/challenges")}
            className="relative border border-zinc-800 hover:border-zinc-600 p-3"
            data-testid="bell-btn"
          >
            <Bell className="w-4 h-4" />
            {(overview?.incoming_challenges || 0) + (overview?.pending_confirmations || 0) > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#FACC15] pulse-dot" />
            )}
          </button>
          <button
            onClick={logout}
            className="border border-zinc-800 hover:border-zinc-600 p-3 text-zinc-500 hover:text-white"
            data-testid="logout-btn"
            title="Logout"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Rank card */}
      <div className="border border-zinc-800 bg-[#121212] p-6 fade-up" data-testid="rank-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="font-mono text-[10px] tracking-[0.3em]" style={{color: tier.color}}>
              {tier.name} TIER
            </div>
            <div className="font-display text-7xl leading-none mt-2">{user?.elo}</div>
            <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-1">ELO RATING</div>

            {next && (
              <div className="mt-5">
                <div className="flex items-center justify-between text-[10px] font-mono tracking-widest text-zinc-500 mb-2">
                  <span>NEXT: {next.name}</span>
                  <span>{pct}%</span>
                </div>
                <div className="h-1 bg-zinc-800">
                  <div className="h-full" style={{ width: `${pct}%`, background: next.color }} />
                </div>
                <div className="font-mono text-[10px] text-zinc-600 mt-2">
                  {next.min - (user?.elo || 0)} POINTS TO {next.name}
                </div>
              </div>
            )}
          </div>
          <RankBadge elo={user?.elo} size="lg" />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <Stat label="GLOBAL #" value={overview ? `#${overview.position}` : "—"} accent />
        <Stat label="W / L" value={`${user?.wins || 0}-${user?.losses || 0}`} />
        <Stat label="STREAK" value={user?.current_streak || 0} />
      </div>

      {/* Pending Confirmations */}
      {pending.length > 0 && (
        <div className="mt-8" data-testid="pending-section">
          <h3 className="font-display text-2xl mb-3">
            <Flame className="inline w-5 h-5 text-[#FACC15] mr-2" />
            AWAITING YOUR CONFIRMATION
          </h3>
          <div className="space-y-3">
            {pending.map((m) => {
              const youWon = m.winner_id === user.user_id;
              const opp = youWon ? m.loser : m.winner;
              return (
                <div key={m.match_id} className="border border-[#FACC15]/40 bg-[#FACC15]/5 p-4" data-testid={`pending-match-${m.match_id}`}>
                  <div className="flex items-center gap-3">
                    <SkaterAvatar user={opp} size={40} />
                    <div className="flex-1">
                      <div className="font-display text-lg">@{opp?.username}</div>
                      <div className="font-mono text-[10px] text-zinc-400">
                        {FORMAT_LABEL[m.format]} • {youWon ? "CLAIMS YOU LOST" : "CLAIMS THEY BEAT YOU"}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => handleConfirm(m.match_id, "CONFIRM")}
                      data-testid={`confirm-match-${m.match_id}`}
                      className="flex-1 bg-white text-black hover:bg-zinc-200 rounded-none font-display tracking-widest h-11"
                    >
                      CONFIRM
                    </Button>
                    <Button
                      onClick={() => handleConfirm(m.match_id, "DISPUTE")}
                      data-testid={`dispute-match-${m.match_id}`}
                      variant="ghost"
                      className="flex-1 border border-zinc-700 rounded-none font-display tracking-widest h-11 hover:bg-zinc-900"
                    >
                      DISPUTE
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mt-8 grid grid-cols-2 gap-3">
        <Link to="/skate/challenges/new" data-testid="quick-challenge" className="block group">
          <div className="border border-zinc-800 group-hover:border-[#FACC15] p-5 transition-colors">
            <Swords className="w-6 h-6 text-[#FACC15] mb-3" strokeWidth={1.8} />
            <div className="font-display text-xl">CHALLENGE</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest">Throw down</div>
          </div>
        </Link>
        <Link to="/skate/leaderboard" data-testid="quick-leaderboard" className="block group">
          <div className="border border-zinc-800 group-hover:border-zinc-600 p-5 transition-colors">
            <Trophy className="w-6 h-6 text-yellow-500 mb-3" strokeWidth={1.8} />
            <div className="font-display text-xl">RANKS</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-widest">Top 50</div>
          </div>
        </Link>
      </div>

      {/* Recent Matches */}
      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-2xl">RECENT MATCHES</h3>
          <Link to="/skate/profile" className="font-mono text-[10px] tracking-widest text-zinc-500 hover:text-white">SEE ALL →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="border border-dashed border-zinc-800 p-8 text-center">
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">NO MATCHES YET</div>
            <p className="text-sm text-zinc-400 mt-2">Challenge a skater to get on the board.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {recent.map((m) => {
              const youWon = m.winner_id === user.user_id;
              const opp = youWon ? m.loser : m.winner;
              const delta = youWon ? m.winner_delta : m.loser_delta;
              return (
                <div key={m.match_id} className="border border-zinc-900 p-4 flex items-center gap-3" data-testid={`match-${m.match_id}`}>
                  <div className={`w-1 h-12 ${youWon ? "bg-green-500" : "bg-[#EF4444]"}`} />
                  <SkaterAvatar user={opp} size={40} />
                  <div className="flex-1">
                    <div className="font-display text-lg">{youWon ? "BEAT" : "LOST TO"} @{opp?.username}</div>
                    <div className="font-mono text-[10px] text-zinc-500">
                      {FORMAT_LABEL[m.format]} • {m.location} • {m.status}
                    </div>
                  </div>
                  {delta != null && m.status === "CONFIRMED" && (
                    <div className={`font-mono text-sm font-bold ${delta >= 0 ? "text-green-500" : "text-[#EF4444]"}`}>
                      {delta >= 0 ? "+" : ""}{delta}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
